/* Additive, reversible desktop-only presentation. Native variant, cart, payment,
   inventory, pickup, shipping and zoom components remain authoritative. */
(() => {
  if (customElements.get('ron-desktop-pdp')) return;
  const desktop = matchMedia('(min-width: 1100px)');
  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };
  class RONDesktopPDP extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      if (!this.section) return;
      this.controller = new AbortController();
      this.observer = new MutationObserver(() => this.schedule());
      this.observe();
      desktop.addEventListener('change', () => this.schedule(), {signal: this.controller.signal});
      this.section.addEventListener('click', event => this.click(event), {signal: this.controller.signal});
      this.section.addEventListener('zoom-media:selected', event => this.select(event.detail.index), {signal: this.controller.signal});
      this.schedule();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.observer?.disconnect();
      this.controller?.abort();
    }
    observe() {
      this.observer.observe(this.section, {childList: true, subtree: true});
      this.observer.observe(this, {attributes:true,attributeFilter:['data-product-type','data-product-handle']});
    }
    schedule() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.observer.disconnect();
        try { desktop.matches ? this.enhance() : this.cleanup(); }
        finally { if (this.isConnected) this.observe(); }
      });
    }
    cleanup() {
      if (!this.section) return;
      this.section.querySelectorAll('[data-ron-generated]').forEach(node => node.remove());
      this.section.querySelectorAll('[data-ron-description-source]').forEach(node => node.removeAttribute('data-ron-description-source'));
      for (const name of ['ron-pdp-desktop','ron-pdp-vendor','ron-pdp-heading','ron-pdp-sizes','ron-pdp-gallery','ron-pdp-active-media']) {
        this.section.querySelectorAll('.'+name).forEach(node => node.classList.remove(name));
      }
    }
    generated(node) { node.dataset.ronGenerated = ''; return node; }
    enhance() {
      const root = this.section.querySelector('.product-information');
      const gallery = root?.querySelector('media-gallery');
      const grid = gallery?.querySelector(':scope > .media-gallery__grid');
      const media = [...grid?.children || []];
      // Unknown, carousel-only, video and model galleries keep their native presentation.
      if ((this.dataset.productType || '').toLowerCase() !== 'apparel' || !root || !media.length || media.some(node => !node.classList.contains('product-media-container--image'))) {
        this.cleanup(); return;
      }
      root.classList.add('ron-pdp-desktop');
      const details = root.querySelector('.product-details');
      const title = details?.querySelector('h1');
      title?.parentElement.classList.add('ron-pdp-heading');
      details?.querySelector('h6 a[href*="/collections/vendors"]')?.closest('h6')?.parentElement.classList.add('ron-pdp-vendor');
      details?.querySelectorAll('variant-picker fieldset').forEach(field => {
        if (/^size$/i.test(field.querySelector('legend')?.textContent.trim() || '')) field.classList.add('ron-pdp-sizes');
      });
      if (!root.querySelector('.ron-pdp-wayfinding')) {
        const nav = this.generated(create('nav','ron-pdp-wayfinding'));
        nav.setAttribute('aria-label','Breadcrumb');
        const home = create('a','','Home'); home.href = window.Shopify?.routes?.root || '/';
        const current = create('span','',title?.textContent || 'Product'); current.setAttribute('aria-current','page');
        nav.append(home,current); root.prepend(nav);
      }
      if (!gallery.classList.contains('ron-pdp-gallery')) this.buildGallery(gallery,media);
      this.buildStory(root,details,media);
    }
    buildGallery(gallery,media) {
      const rail = this.generated(create('div','ron-pdp-thumbs'));
      rail.setAttribute('aria-label','Product images');
      media.forEach((node,index) => {
        const button = create('button','ron-pdp-thumb'); button.type = 'button';
        button.dataset.ronImage = index; button.setAttribute('aria-label',`View image ${index+1} of ${media.length}`);
        const image = node.querySelector('img').cloneNode(); image.alt = ''; image.sizes = '76px'; image.loading = 'lazy';
        button.append(image); rail.append(button);
      });
      const caption = this.generated(create('div','ron-pdp-caption'));
      const count = create('span','ron-pdp-count'); count.setAttribute('aria-live','polite');
      const controls = create('span');
      for (const [step,label,glyph] of [[-1,'Previous image','←'],[1,'Next image','→']]) {
        const button = create('button','',glyph); button.type = 'button'; button.dataset.ronStep = step; button.setAttribute('aria-label',label); controls.append(button);
      }
      caption.append(count,controls); gallery.append(rail,caption); gallery.classList.add('ron-pdp-gallery');
      this.select(0);
    }
    select(index) {
      const gallery = this.section?.querySelector('.ron-pdp-gallery');
      const media = [...gallery?.querySelectorAll(':scope > .media-gallery__grid > li') || []];
      if (!media.length) return;
      this.selectedIndex = (Number(index) + media.length) % media.length;
      media.forEach((node,i) => node.classList.toggle('ron-pdp-active-media',i === this.selectedIndex));
      gallery.querySelectorAll('.ron-pdp-thumb').forEach((node,i) => node.setAttribute('aria-pressed',String(i === this.selectedIndex)));
      gallery.querySelector('.ron-pdp-count').textContent = `Image ${this.selectedIndex+1} / ${media.length}`;
    }
    click(event) {
      if (!desktop.matches) return;
      const thumb = event.target.closest('[data-ron-image]');
      const step = event.target.closest('[data-ron-step]');
      if (thumb) this.select(thumb.dataset.ronImage);
      if (step) this.select((this.selectedIndex || 0) + Number(step.dataset.ronStep));
      const fit = event.target.closest('[data-ron-fit-link]');
      if (fit) {
        event.preventDefault();
        const target = this.section.querySelector('.ron-pdp-story-description details');
        if (target) { target.open = true; target.scrollIntoView({block:'center',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'}); }
      }
    }
    buildStory(root,details,media) {
      const description = details?.querySelector('rte-formatter[class*="product_description"]');
      const source = description?.closest('accordion-custom');
      const unsafe = !description || description.querySelector('[id], input, button, select, textarea, form, iframe, video, audio, script, object, embed, [contenteditable]') || [...description.querySelectorAll('*')].some(node => node.tagName.includes('-') || [...node.attributes].some(attr => /^on/i.test(attr.name)));
      if (!source || media.length < 2 || unsafe) {
        root.querySelectorAll('.ron-pdp-story,.ron-pdp-lead,.ron-pdp-fit').forEach(node=>node.remove());
        root.querySelectorAll('[data-ron-description-source]').forEach(node=>node.removeAttribute('data-ron-description-source'));
        this.descriptionSignature = null; return;
      }
      const signature = [this.dataset.productHandle,description.innerHTML,...media.map(node=>node.querySelector('img')?.getAttribute('src'))].join('|');
      if (this.descriptionSignature !== signature) root.querySelectorAll('.ron-pdp-fit,.ron-pdp-policy-summary,.ron-pdp-lead').forEach(node=>node.remove());
      this.buildGuidance(root,details,description);
      let story = root.querySelector('.ron-pdp-story');
      if (story && this.descriptionSignature === signature) { source.dataset.ronDescriptionSource = ''; return; }
      story?.remove();
      story = this.generated(create('section','ron-pdp-story'));
      story.setAttribute('aria-label','Product details');
      const copy = create('div');
      copy.append(create('p','ron-pdp-story-kicker','ALWAYS NEXT. / THE FINER POINTS'));
      const isTrucker = this.dataset.productHandle === 'bb-trucker-jacket' && /cotton duck canvas/i.test(description.textContent);
      copy.append(create('h2','',isTrucker ? 'Canvas with character.' : 'The finer points.'));
      const body = create('div','ron-pdp-story-description');
      const clone = create('div');
      for (const child of description.childNodes) clone.append(child.cloneNode(true));
      const intro = clone.querySelector('p');
      if (intro) body.append(intro);
      const disclosure = create('details'); disclosure.append(create('summary','','All product details'));
      disclosure.id = `RonPDPDetails-${this.section.id}`;
      while (clone.firstChild) disclosure.append(clone.firstChild);
      body.append(disclosure); copy.append(body);
      const images = create('div','ron-pdp-story-images');
      for (const node of media.slice(1,3)) {
        const image = node.querySelector('img').cloneNode(); image.sizes = '(min-width: 1100px) 430px, 100vw'; image.loading = 'lazy'; images.append(image);
      }
      story.append(copy,images); root.append(story);
      source.dataset.ronDescriptionSource = '';
      this.descriptionSignature = signature;
      if (isTrucker && !root.querySelector('.ron-pdp-lead')) {
        const lead = this.generated(create('p','ron-pdp-lead','Cotton duck canvas. Contrast denim. Paint-splatter finish.'));
        details.querySelector('product-price')?.after(lead);
      }
    }
    buildGuidance(root,details,description) {
      if (!root.querySelector('.ron-pdp-fit') && /\bfit\b|measures/i.test(description.textContent)) {
        const fit = this.generated(create('p','ron-pdp-fit'));
        const fact = [...description.querySelectorAll('li')].find(node=>/^(regular|relaxed|slim|oversized|standard) fit\b/i.test(node.textContent.trim()));
        if (fact) fit.append(create('span','',fact.textContent.trim()));
        const link = create('a','','Size & fit'); link.href = `#RonPDPDetails-${this.section.id}`; link.dataset.ronFitLink=''; fit.append(link);
        details.querySelector('variant-picker')?.after(fit);
      }
      if (!root.querySelector('.ron-pdp-policy-summary')) {
        const policy = [...details.querySelectorAll('accordion-custom')].find(node=>/shipping.*returns/i.test(node.querySelector('summary')?.textContent || ''));
        const facts = [...policy?.querySelectorAll('li') || []].filter(node=>/21 days|store credit only/i.test(node.textContent));
        if (facts.length) {
          const summary = this.generated(create('div','ron-pdp-policy-summary'));
          for (const fact of facts) summary.append(create('p','',fact.textContent.trim()));
          summary.append(create('small','','Eligibility and exclusions below.'));
          policy.before(summary);
        }
      }
    }
  }
  customElements.define('ron-desktop-pdp',RONDesktopPDP);
})();
